<template>
  <div
    :class="[
      'effect-cover',
      {
        'effect-cover--editing': showInput
      }
    ]"
    id="effectCover"
  >
    <div class="effect-cover__hover" :style="effectStyle" />
    <svg
      class="effect-cover__grid"
      :viewBox="`0 0 ${svgWidth} 480`"
      v-show="gridShowing"
    >
      <linearGradient
        spreadMethod="repeat"
        x1="0"
        x2="0"
        y1="0"
        y2=".08"
        id="gridVerticalGradient"
      >
        <stop offset="50%" stop-color="rgba(150,150,150,.4)" />
        <stop offset="50%" stop-color="transparent" />
      </linearGradient>
      <pattern
        id="gridPattern"
        width="60"
        height="60"
        viewBox="0 0 60 60"
        patternUnits="userSpaceOnUse"
        :x="(svgWidth / 2) % 60"
      >
        <rect
          x="59"
          y="0"
          width="1"
          height="60"
          fill="url(#gridVerticalGradient)"
          id="gridVerticalRect"
        />
        <use href="#gridVerticalRect" transform="translate(60) rotate(90)" />
      </pattern>
      <rect
        x=".5"
        y=".5"
        :width="svgWidth - 1.5"
        height="478.5"
        fill="url(#gridPattern)"
      />
      <rect
        fill="rgba(0,0,0,.15)"
        height="480"
        width="1"
        y="0"
        :x="svgWidth / 2 - 0.5"
      />
      <rect
        fill="rgba(0,0,0,.15)"
        height="1"
        :width="svgWidth"
        x="0"
        y="239.5"
      />
    </svg>
    <div
      class="effect-cover__hover"
      v-for="(s, i) in preselectedEffectStyles"
      :key="i"
      :style="s"
    />
    <div
      class="effect-cover__hover effect-cover__hover--secondary"
      v-if="secondarySelection"
      :style="secondarySelection.style"
      @click="secondarySelection.action"
    />
    <div
      class="effect-cover__snap"
      :class="{ 'effect-cover__snap--current': snappingX }"
      :style="{ left: snapX + 'px' }"
    />
    <div
      class="effect-cover__snap effect-cover__snap--horizontal"
      :class="{ 'effect-cover__snap--current': snappingY }"
      :style="{ top: snapY + 'px' }"
    />
    <template v-for="effect in selectedEffects">
      <line-selection
        v-if="effect.content.shapeLinear"
        :key="effect.id"
        :selectedEffect="effect"
      />
      <effect-selection
        v-else
        :key="effect.id"
        :selectedEffect="effect"
        :clipping="clipping"
      />
    </template>
  </div>
</template>
<script>
// import { notification } from 'ant-design-vue';
import { getEffectBoxStyle, rotateBy } from '../../utils';
import { shapePreserveRatio } from '../../utils/shapes';
import EffectSelection from './EffectSelection';
import LineSelection from './LineSelection';

export default {
  components: {
    EffectSelection,
    LineSelection
  },
  data() {
    return {
      snappingX: false,
      snapX: 0,
      snappingY: false,
      snapY: 0
    };
  },
  computed: {
    allEffects() {
      return this.$store.state.effects;
    },
    activeFragment() {
      return this.$store.getters.activeFragment;
    },
    scale() {
      return this.$store.state.playerScale / 100;
    },
    svgWidth() {
      // return `0 0 ${this.$store.state.screenRatio * 480} 480`;
      return this.$store.state.screenRatio * 480;
    },
    gridShowing() {
      return this.$store.state.gridShowing;
    },
    hoveringEffect() {
      return this.allEffects[this.$store.state.hoveringEffectId] || null;
    },
    effectStyle() {
      if (!this.playing && !this.dragging && this.hoveringEffect && !this.selectionDrawing) {
        const ret = getEffectBoxStyle(this.hoveringEffect);
        ['left', 'top', 'width', 'height'].forEach(key => {
          ret[key] = parseFloat(ret[key]) * this.scale + 'px';
        });
        return ret;
      }
      return {
        display: 'none'
      };
    },
    clipping() {
      return this.$store.state.clipping;
    },
    selectedEffects() {
      const selectionLookup = {};
      const selectedEffectIds = this.$store.state.selectedEffectIds;
      if (selectedEffectIds.length <= 1) {
        // keep single selection as-is
        if (selectedEffectIds[0] === 'BACKGROUND_SELECT') {
          return [];
        }
        return selectedEffectIds.map(id => this.allEffects[id]).filter(ef => !!ef);
      }
      selectedEffectIds.forEach(id => {
        selectionLookup[id] = true;
      });
      return this.activeFragment
        ? this.activeFragment.effects.filter(id => !!selectionLookup[id]).map(id => this.allEffects[id])
        : [];
    },
    selectionDrawing() {
      return this.$store.state.selectionDrawing;
    },
    preselectedEffectStyles() {
      const selectionLookup = {};
      const { preselectedEffectIds } = this.$store.state;
      preselectedEffectIds.forEach(id => {
        selectionLookup[id] = true;
      });
      return this.selectionDrawing && this.activeFragment
        ? this.activeFragment.effects.filter(id => !!selectionLookup[id]).map(id => {
          const ef = this.allEffects[id];
          const ret = getEffectBoxStyle(ef);
          ['left', 'top', 'width', 'height'].forEach(key => {
            ret[key] = parseFloat(ret[key]) * this.scale + 'px';
          });
          return ret;
        })
        : [];
    },
    dragging: {
      get() {
        return this.$store.state.effectDragging;
      },
      set(value) {
        this.$store.commit('handleEffectDrag', value);
      }
    },
    playing() {
      return this.$store.state.playing;
    },
    showInput() {
      return this.$store.state.effectEditing;
    },
    unrotatable() {
      // return this.selectedEffect && this.selectedEffect.type === 'textContainer';
      return false;
    },
    secondarySelection() {
      let sec;
      if (this.selectedEffects.length === 1) {
        const ef = this.selectedEffects[0];
        if (ef.container) {
          sec = this.allEffects[ef.container];
        }
      }
      if (sec) {
        const ret = getEffectBoxStyle(sec);
        ['left', 'top', 'width', 'height'].forEach(key => {
          ret[key] = parseFloat(ret[key]) * this.scale + 'px';
        });
        let action = () => { };
        if (sec.type === 'group') {
          ret.pointerEvents = 'auto';
          action = () => {
            this.$store.commit('handleEffectSelect', [sec.id]);
          };
        }
        return {
          style: ret,
          action
        };
      }
      return null;
    }
  },
  methods: {
    /**
     * TODO: snap
     * @param{MouseEvent}e
     */
    handleMouseMove(e) {
      function getSnapDiff(x, x0) {
        const val = typeof x0 === 'number' ? x0 : x0.value;
        const factor = typeof x0 === 'number' ? 1 : x0.factor;
        return (x - val) / factor;
      }

      if (this.dragging && this.prevEffects) {
        let preserve = false;
        const isMultiple = this.prevEffects.length > 1;

        let normDeltaX;
        let normDeltaY;

        snap: if (!this.dragging.rotate) {
          normDeltaX = (e.clientX - this.dragging.clientX) / this.scale;
          normDeltaY = (e.clientY - this.dragging.clientY) / this.scale;

          if (e.altKey || !this.$store.state.snapEnabled) {
            this.releaseSnap();
            break snap;
          }

          const prevEffectIds = {};
          this.prevEffects.forEach(p => {
            const ef = p.effect;
            prevEffectIds[ef.id] = 1;
          });
          //#region snap
          const cur = this.$store.state.currentProgress - this.activeFragment.start;
          const bcr = this.$el.getBoundingClientRect();
          const allPoints = {
            x: [bcr.left, bcr.width / 2 + bcr.left, bcr.width + bcr.left],
            y: [bcr.top, bcr.height / 2 + bcr.top, bcr.height + bcr.top]
          };

          this.activeFragment.effects.forEach(id => {
            if (!prevEffectIds[id]) {
              const ef = this.allEffects[id];
              if (ef.start <= cur && ef.start + ef.duration > cur) {
                console.log('between');
                const efr = ef.content;

                const { top, left, width, height } = efr;

                // TODO: rotation-aware hit test
                const center = {
                  x: left + width / 2,
                  y: top + height / 2
                };
                const rotatedPoints = [
                  { x: left, y: top },
                  { x: left + width, y: top },
                  { x: left + width, y: top + height },
                  { x: left, y: top + height }
                ].map(p => rotateBy(p, center, efr.rotate)).concat(center);

                // TODO: normalize

                rotatedPoints.forEach(pt => {
                  allPoints.x.push(pt.x * this.scale + bcr.left);
                  allPoints.y.push(pt.y * this.scale + bcr.top);
                });
              }
            }
          });

          const xs = [];
          const ys = [];
          let proportional = false;
          let proportionalDone = false;
          const ef = this.allEffects[this.dragging.effectId];

          if (this.dragging.dir) {
            if (isMultiple || e.shiftKey) {
              proportional = true;
            }
            const efc = ef.content;

            switch (ef.type) {
              case 'image':
                if (!efc.repeat) {
                  proportional = true;
                }
                break;
              case 'video':
              case 'group':
              case 'lottie':
              case 'sticker':
                proportional = true;
                break;
              case 'shape':
              case 'container':
                if (shapePreserveRatio[efc.shape]) {
                  proportional = true;
                }
            }
            const { top, left, width, height, rotate } = this.draggingEffect;
            const center = {
              x: left + width / 2,
              y: top + height / 2
            };
            const curPoint = {
              x: this.dragging.dir[1] === 'l' ? left : left + width,
              y: this.dragging.dir[0] === 't' ? top : top + height
            };

            let rotated = rotateBy(curPoint, center, rotate);
            let totalRotate = rotate;
            if (ef.container) {
              const container = this.allEffects[ef.container];
              const { top, left, width, height, rotate } = container.content;
              rotated = rotateBy(rotated, { x: width / 2, y: height / 2 }, rotate);
              rotated.y += top;
              rotated.x += left;
              totalRotate += rotate;
            }

            if (proportional) {
              // TODO: get projection
              const { x: projX } = rotateBy({
                x: normDeltaX,
                y: normDeltaY
              }, { x: 0, y: 0 }, -totalRotate);

              // rotate projection back
              const { x, y } = rotateBy({
                x: projX,
                y: projX * height / width * ({ tl: 1, br: 1 }[this.dragging.dir] ? 1 : -1)
              }, { x: 0, y: 0 }, totalRotate);

              xs.push(
                {
                  value: (rotated.x + x) * this.scale + bcr.left,
                  factor: 1,
                  ref: x
                },
                {
                  value: (center.x + x / 2) * this.scale + bcr.left,
                  factor: .5,
                  ref: x
                }
              );
              ys.push(
                {
                  value: (rotated.y + y) * this.scale + bcr.top,
                  factor: 1,
                  ref: y
                },
                {
                  value: (center.y + y / 2) * this.scale + bcr.top,
                  factor: .5,
                  ref: y
                }
              );
            } else {
              xs.push((rotated.x + normDeltaX) * this.scale + bcr.left);
              ys.push((rotated.y + normDeltaY) * this.scale + bcr.top);
              xs.push({
                value: (center.x + normDeltaX / 2) * this.scale + bcr.left,
                factor: .5
              });
              ys.push({
                value: (center.y + normDeltaY / 2) * this.scale + bcr.top,
                factor: .5
              });
            }
          } else {
            this.prevEffects.forEach(p => {
              const { x, y, w, h, r } = p;
              const center = {
                x: x + w / 2,
                y: y + h / 2
              };

              [
                { x, y },
                { x: x + w, y },
                { x: x + w, y: y + h },
                { x, y: y + h }
              ].map(pt => rotateBy(pt, center, r))
                .concat(center)
                .forEach(pt => {
                  if (ef.container) {
                    const container = this.allEffects[ef.container];
                    const { top, left, width, height, rotate } = container.content;
                    const rotated = rotateBy(pt, { x: width / 2, y: height / 2 }, rotate);
                    pt.x = rotated.x + left;
                    pt.y = rotated.y + top;
                  }
                  xs.push((pt.x + normDeltaX) * this.scale + bcr.left);
                  ys.push((pt.y + normDeltaY) * this.scale + bcr.top);
                });
            });
          }

          const minX = Math.min.apply(
            Math,
            [].concat.apply([], allPoints.x.map(x => xs.map(x0 => Math.abs(getSnapDiff(x, x0)))))
          );
          if (minX < 10) {
            allPoints.x.some(x => {
              return xs.some(x0 => {
                const normDiff = getSnapDiff(x, x0);
                if (Math.abs(normDiff) === minX) {
                  if (proportional) {
                    if (!x0.ref) {
                      return false;
                    }
                    const ratio = (x0.ref + normDiff / this.scale) / x0.ref;
                    normDeltaX *= ratio;
                    normDeltaY *= ratio;
                    proportionalDone = true;
                  } else {
                    normDeltaX += normDiff / this.scale;
                  }
                  clearTimeout(this.snapXTimeout);
                  this.snapXTimeout = null;

                  this.snappingX = true;
                  this.snapX = x - bcr.left;

                  return true;
                }
                return false;
              });
            });
          } else {
            if (!this.snapXTimeout) {
              this.snapXTimeout = setTimeout(() => {
                this.snapXTimeout = null;
                this.snappingX = false;
              }, 50);
            }
          }

          if (proportionalDone) {
            break snap;
          }

          const minY = Math.min.apply(
            Math,
            [].concat.apply([], allPoints.y.map(y => ys.map(y0 => Math.abs(getSnapDiff(y, y0)))))
          );

          if (minY < 10) {
            allPoints.y.some(y => {
              return ys.some(y0 => {
                const normDiff = getSnapDiff(y, y0);
                if (Math.abs(normDiff) === minY) {
                  if (proportional) {
                    if (!y0.ref) {
                      return false;
                    }
                    const ratio = (y0.ref + normDiff / this.scale) / y0.ref;
                    normDeltaX *= ratio;
                    normDeltaY *= ratio;
                  } else {
                    normDeltaY += normDiff / this.scale;
                  }
                  clearTimeout(this.snapYTimeout);
                  this.snapYTimeout = null;

                  this.snappingY = true;
                  this.snapY = y - bcr.top;

                  return true;
                }
                return false;
              });
            });
          } else {
            if (!this.snapYTimeout) {
              this.snapYTimeout = setTimeout(() => {
                this.snapYTimeout = null;
                this.snappingY = false;
              }, 50);
            }
          }
          //#endregion
        }

        this.prevEffects.forEach(prevEffect => {
          let actualDir = this.dragging.dir || '';
          const selectedEffect = prevEffect.effect;
          const containerEffect = selectedEffect.container ? this.allEffects[selectedEffect.container] : null;

          const isGroupEffect = selectedEffect.type === 'group';

          let deltaX;
          let deltaY;
          if (this.dragging.rotate) {
            console.log('rotating');
            const [centerX, centerY] = this.dragging.rotate;

            deltaX = e.clientX - centerX;
            deltaY = e.clientY - centerY;

            let rotation;
            if (deltaX) {
              rotation = (Math.atan(deltaY / deltaX) * 180 / Math.PI + (deltaX > 0 ? 180 : 0)) - 90;
            } else {
              rotation = deltaY > 0 ? 180 : 0;
            }

            const containerRotation = containerEffect ? containerEffect.content.rotate : 0;
            const containerFlip = containerEffect && containerEffect.content.flip && containerEffect.content.flip !== 'none';

            const rotate = (rotation - containerRotation + prevEffect.r - this.draggingEffect.rotate) * (containerFlip ? -1 : 1);
            selectedEffect.content = {
              ...selectedEffect.content,
              rotate: e.shiftKey ? Math.round(rotate / 45) * 45 : Math.round(rotate)
            };
            return;
          }

          if (
            selectedEffect.type === 'shape'
            && selectedEffect.content.shapeLinear
            && actualDir
          ) {
            return;
          }

          deltaX = normDeltaX;
          deltaY = normDeltaY;

          // TODO: get rotated deltas when resizing, but only use the dragged one
          const efRotate = this.draggingEffect.rotate;
          if (actualDir) {
            const zoom = prevEffect.w / this.draggingEffect.width;
            if (efRotate) {
              const unrotatedDelta = rotateBy({ x: deltaX, y: deltaY }, { x: 0, y: 0 }, -efRotate);
              deltaX = unrotatedDelta.x * zoom;
              deltaY = unrotatedDelta.y * zoom;
            } else {
              deltaX *= zoom;
              deltaY *= zoom;
            }
          }

          if (containerEffect) {
            const containerEFC = containerEffect.content;
            const unrotatedDelta = rotateBy(
              { x: deltaX, y: deltaY },
              { x: 0, y: 0 },
              (actualDir && containerEFC.flip && containerEFC.flip !== 'none' && 2 * efRotate || 0) - containerEFC.rotate
            ); // normalize rotation when flipped.

            deltaX = unrotatedDelta.x;
            deltaY = unrotatedDelta.y;
            switch (containerEFC.flip) {
              case 'horizontal':
                actualDir = actualDir.replace(/[lr]/, t => t === 'l' ? 'r' : 'l');
                deltaX = -deltaX;
                break;
              case 'vertical':
                actualDir = actualDir.replace(/[tb]/, t => t === 't' ? 'b' : 't');
                deltaY = -deltaY;
                break;
            }
          }

          label: if (
            actualDir
            && (
              selectedEffect.type !== 'text' && selectedEffect.type !== 'textContainer' && selectedEffect.type !== 'wordArt'
              || isMultiple || e.shiftKey
            )
          ) {
            if (!e.shiftKey && !isMultiple) {
              if (selectedEffect.type === 'image' && selectedEffect.content.repeat) {
                break label;
              }
              if (
                (selectedEffect.type === 'shape' || selectedEffect.type === 'container')
                && !shapePreserveRatio[selectedEffect.content.shape]
              ) {
                break label;
              }
            }
            preserve = true;
            const prevRatio = (
              actualDir === 'tr' || actualDir === 'bl' ? -1 : 1
            ) * prevEffect.h / prevEffect.w;
            deltaY = deltaX * prevRatio;
          } else if (e.shiftKey) {
            if (Math.abs(deltaX / deltaY) >= 1) {
              deltaY = 0;
            } else {
              deltaX = 0;
            }
          }

          let nextEfc;

          switch (actualDir) {
            case 'tr':
              nextEfc = {
                ...selectedEffect.content,
                width: Math.max(prevEffect.w + deltaX, 1),
                top: prevEffect.y + deltaY,
                height: Math.max(prevEffect.h - deltaY, 1)
              };
              break;
            case 'br':
              nextEfc = {
                ...selectedEffect.content,
                width: Math.max(prevEffect.w + deltaX, 1),
                height: Math.max(prevEffect.h + deltaY, 1)
              };
              break;
            case 'bl':
              nextEfc = {
                ...selectedEffect.content,
                width: Math.max(prevEffect.w - deltaX, 1),
                left: prevEffect.x + deltaX,
                height: Math.max(prevEffect.h + deltaY, 1)
              };
              break;
            case 'tl':
              nextEfc = {
                ...selectedEffect.content,
                width: Math.max(prevEffect.w - deltaX, 1),
                top: prevEffect.y + deltaY,
                left: prevEffect.x + deltaX,
                height: Math.max(prevEffect.h - deltaY, 1)
              };
              break;
            default:
              nextEfc = {
                ...selectedEffect.content,
                left: prevEffect.x + deltaX,
                top: prevEffect.y + deltaY
              };
          }

          // TODO: alignment of vertices
          if (efRotate && actualDir) {
            const center = { x: prevEffect.w / 2, y: prevEffect.h / 2 };
            const nextCenter = { x: nextEfc.width / 2, y: nextEfc.height / 2 };
            let anchor, nextAnchor;

            switch (actualDir) {
              case 'tr':
                // align bottom left
                {
                  anchor = rotateBy(
                    { x: 0, y: prevEffect.h },
                    center,
                    efRotate
                  );

                  nextAnchor = rotateBy(
                    { x: 0, y: nextEfc.height },
                    nextCenter,
                    efRotate
                  );
                }
                break;
              case 'br':
                // align top left
                {
                  anchor = rotateBy(
                    { x: 0, y: 0 },
                    center,
                    efRotate
                  );

                  nextAnchor = rotateBy(
                    { x: 0, y: 0 },
                    nextCenter,
                    efRotate
                  );
                }
                break;
              case 'bl':
                // align top right
                {
                  anchor = rotateBy(
                    { x: prevEffect.w, y: 0 },
                    center,
                    efRotate
                  );

                  nextAnchor = rotateBy(
                    { x: nextEfc.width, y: 0 },
                    nextCenter,
                    efRotate
                  );
                }
                break;
              case 'tl':
                // align bottom right
                {
                  anchor = rotateBy(
                    { x: prevEffect.w, y: prevEffect.h },
                    center,
                    efRotate
                  );

                  nextAnchor = rotateBy(
                    { x: nextEfc.width, y: nextEfc.height },
                    nextCenter,
                    efRotate
                  );
                }
                break;
            }
            if (anchor) {
              nextEfc.top = prevEffect.y + anchor.y - nextAnchor.y;
              nextEfc.left = prevEffect.x + anchor.x - nextAnchor.x;
            }
          }

          if (deltaX || deltaY) {
            this.dragged = true;
          }

          if (nextEfc) {
            const limit = this.$store.state.screenRatio < 1 ? 960 : 1700;
            if (
              nextEfc.width !== prevEffect.w
              || nextEfc.height !== prevEffect.h
            ) {
              if (preserve) {
                if (nextEfc.width > limit || nextEfc.height > limit) {
                  const factor = Math.min(limit / nextEfc.width, limit / nextEfc.height);
                  nextEfc.width *= factor;
                  nextEfc.height *= factor;
                  if (actualDir[1] === 'l') {
                    nextEfc.left = prevEffect.x + prevEffect.w - nextEfc.width;
                  }
                  if (actualDir[0] === 't') {
                    nextEfc.top = prevEffect.y + prevEffect.h - nextEfc.height;
                  }
                }
              } else {
                if (nextEfc.width > limit || nextEfc.height > limit) {
                  if (nextEfc.width > limit) {
                    nextEfc.width = limit;
                    if (actualDir[1] === 'l') {
                      nextEfc.left = prevEffect.x + prevEffect.w - nextEfc.width;
                    }
                  }
                  if (nextEfc.height > limit) {
                    nextEfc.height = limit;
                    if (actualDir[0] === 't') {
                      nextEfc.top = prevEffect.y + prevEffect.h - nextEfc.height;
                    }
                  }
                }
              }

              if (isGroupEffect) {
                const factor = nextEfc.width / prevEffect.w;
                prevEffect.children.forEach(prevChild => {
                  const childEffect = prevChild.effect;
                  [
                    ['top', 'y'],
                    ['left', 'x'],
                    ['width', 'w'],
                    ['height', 'h']
                  ].forEach(([k, pk]) => {
                    if (
                      k === 'height'
                      && childEffect.type === 'shape'
                      && childEffect.content.shapeLinear
                    ) {
                      return;
                    }
                    childEffect.content[k] = prevChild[pk] * factor;
                  });
                });
              }
            }
            if (this.dragged) {
              selectedEffect.content = nextEfc;
            }
          }
        });
      }
    },
    handleMouseUp() {
      if (this.dragging) {
        if (!this.dragging.dir && !this.dragging.rotate && !this.dragged) {
          const { next: nextEffectId, ctrlKey, childId } = this.dragging;
          const prevIds = this.$store.state.selectedEffectIds;
          if (nextEffectId) {
            if (ctrlKey) {
              this.$store.dispatch('selectMultipleEffects', prevIds.filter(id => id !== nextEffectId).concat(nextEffectId));
            } else {
              this.$store.commit('handleEffectSelect', [nextEffectId]);
            }
          } else {
            if (ctrlKey) {
              this.$store.commit('handleEffectSelect', prevIds.filter(id => id !== this.dragging.effectId));
            } else if (childId) {
              this.$store.commit('handleEffectSelect', [childId]);
            }
          }
        }

        if (this.prevEffects) {
          const arr = [];
          this.prevEffects.forEach(pr => {
            const ef = pr.effect;
            if (ef.container) {
              const parentEffect = this.allEffects[ef.container];

              if (parentEffect && parentEffect.type === 'group') {
                arr.push(parentEffect);
              }
            }
          });

          this.$store.dispatch('adjustGroupSize', arr);
        }
        this.releaseSnap();
        this.dragging = false;
      }
    },
    releaseSnap() {
      if (this.snappingX && !this.snapXTimeout) {
        this.snapXTimeout = setTimeout(() => {
          this.snapXTimeout = null;
          this.snappingX = false;
        }, 50);
      }
      if (this.snappingY && !this.snapYTimeout) {
        this.snapYTimeout = setTimeout(() => {
          this.snapYTimeout = null;
          this.snappingY = false;
        }, 50);
      }
    }
  },

  watch: {
    dragging(val, prev) {
      if (val && !prev) {
        document.body.classList.add('unselectable');
        this.dragged = false;

        this.prevEffects = this.selectedEffects.map(
          function processPrevEffect(effect) {
            return {
              x: effect.content.left,
              y: effect.content.top,
              w: effect.content.width,
              h: effect.content.height,
              r: effect.content.rotate,
              effect,
              children: effect.type === 'group'
                ? effect.children.map(c => processPrevEffect(this.allEffects[c]))
                : null
            };
          }.bind(this)
        );

        const effect = this.allEffects[this.dragging.effectId];
        const { top, left, width, height, rotate } = effect.content;
        this.draggingEffect = { top, left, width, height, rotate };
      } else if (prev && !val) {
        setTimeout(() => {
          document.body.classList.remove('unselectable');
        }, 0);
      }
    }
  },
  mounted() {
    document.addEventListener('mousemove', this.handleMouseMove);
    document.addEventListener('mouseup', this.handleMouseUp);
  },
  beforeDestroy() {
    document.removeEventListener('mousemove', this.handleMouseMove);
    document.removeEventListener('mouseup', this.handleMouseUp);

    if (this.snapXTimeout) {
      clearTimeout(this.snapXTimeout);
    }

    if (this.snapYTimeout) {
      clearTimeout(this.snapYTimeout);
    }
  }
}
</script>

<style>
.effect-cover {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  pointer-events: none;
}

.effect-cover--editing {
  z-index: 3;
}

.effect-cover__grid {
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
}

.effect-cover__hover {
  position: absolute;
  border: 1px solid rgba(64, 169, 255, 0.45);
}

.effect-cover__hover--secondary {
  border: 1px dashed #333;
}

.effect-cover__hover--hidden {
  opacity: 0;
  transition: opacity 0.2s;
}

.effect-cover__hover--hidden:hover {
  opacity: 1;
}

.effect-cover__selection {
  position: absolute;
  outline: 2px solid rgba(64, 169, 255, 0.7);
  outline-offset: -2px;
  pointer-events: auto;
  z-index: 0;
  cursor: move;
}

.effect-cover--editing .effect-cover__selection {
  background: rgba(255, 255, 255, 0.55);
}

.effect-cover__selection:before {
  bottom: 100%;
  left: 50%;
  position: absolute;
  content: "";
  border-left: 2px solid rgba(64, 169, 255, 0.7);
  margin-left: -1px;
  height: 20px;
  pointer-events: none;
}

.effect-cover__handle {
  position: absolute;
  width: 12px;
  height: 12px;
  border: 1px solid #6c6c6c;
  background: #fff;
  border-radius: 9px;
  margin: -5px;
  z-index: 1;
}

.effect-cover__handle--tl,
.effect-cover__handle--tr {
  top: 0;
}

.effect-cover__handle--tr,
.effect-cover__handle--br {
  right: 0;
}

.effect-cover__handle--bl,
.effect-cover__handle--br {
  bottom: 0;
}

.effect-cover__handle--tl,
.effect-cover__handle--bl {
  left: 0;
}

.effect-cover__handle--tlc{
  left:0;
  top:50%;
  transform: translateY(-50%);
  cursor:e-resize !important;
}

.effect-cover__handle--trc{
  right:0;
  top:50%;
  transform: translateY(-50%);
  cursor:e-resize !important;
}

.effect-cover__rotate {
  position: absolute;
  border: 1px solid #6c6c6c;
  background: #fff;
  top: -24px;
  left: 50%;
  width: 14px;
  height: 14px;
  border-radius: 9px;
  margin: -7px;
  cursor: pointer;
}

.effect-cover__selection--unrotatable:before,
.effect-cover__selection--unrotatable .effect-cover__rotate {
  display: none;
}

.effect-cover__rotate:hover,
.effect-cover__rotate:active {
  background: #d7d7d7;
}

.effect-cover__measure {
  /* used in detectLines */
  position: absolute;
  bottom: 100%;
  left: 0;
  width: 100%;
  height: auto !important;
  visibility: hidden;
  white-space: pre-wrap;
  word-wrap: break-word;
  font-feature-settings: "kern" off, "tnum" off;
  font-kerning: none;
  padding: 0 1px;
}

.effect-cover__measure:before {
  content: "";
  margin-right: -1px;
  display: inline-block;
  vertical-align: middle;
  height: 1em;
  width: 1px;
}

.effect-cover__input {
  position: absolute;
  top: -1px;
  left: -1px;
  width: calc(100% + 2px);
  height: calc(100% + 2px);
  border: 0;
  border-radius: 0;
  color: #333;
  background: transparent;
  outline: none;
  padding: 0 2px;
  overflow-x: hidden;
  resize: none;
  font-feature-settings: "kern" off, "tnum" off;
}

.effect-cover__input::-webkit-scrollbar {
  display: none !important;
}

.effect-cover__snap {
  position: absolute;
  top: 0;
  width: 1px;
  height: 100%;
  pointer-events: none;
  border-left: 1px dashed rgba(40, 200, 140, 0.8);
  transition: opacity 0.1s;
  opacity: 0;
  z-index: 0;
}

.effect-cover__snap--horizontal {
  left: 0;
  height: 1px;
  width: 100%;
  border-left: 0;
  border-top: 1px dashed rgba(40, 200, 140, 0.8);
}

.effect-cover__snap--current {
  opacity: 1;
}
</style>